import { summarizeOrderPayments } from "@/utils/orderPaymentSummary";
import { summarizeOrderSpecifiedPaymentTotal } from "@/utils/orderSpecifiedPaymentTotal";
import { summarizeRefundAmounts } from "@/utils/refundAmountSummary";

export type OrderPathFilterMode = "include" | "exclude";
export type OrderPathPresentationMode = "separate" | "grouped";

export interface OrderPathFilter {
  selectedKeys: string[];
  mode: OrderPathFilterMode;
  presentationMode?: OrderPathPresentationMode;
}

export interface OrderPathOption {
  key: string;
  label: string;
}

export interface OrderPathDefinition {
  id: string;
  label: string;
  feePathCode: string;
}

export interface OrderPathGroupDefinition {
  key: string;
  label: string;
  memberIds: string[];
  feeSourceId: string;
}

export interface ResolvedOrderPath extends OrderPathOption {
  sourceId: string;
  sourceLabel: string;
  feePathCode: string;
}

export const ORDER_PATH_DEFINITIONS: OrderPathDefinition[] = [
  { id: "self", label: "PC 쇼핑몰", feePathCode: "cafe24" },
  { id: "mobile", label: "모바일웹", feePathCode: "mobile" },
];

export const ORDER_PATH_GROUPS: OrderPathGroupDefinition[] = [
  {
    key: "owned-mall",
    label: "카페24",
    memberIds: ["self", "mobile"],
    feeSourceId: "self",
  },
];

export const OWNED_MALL_ORDER_PATH_GROUP = ORDER_PATH_GROUPS[0];

export const DEFAULT_ORDER_PATH_OPTIONS: OrderPathOption[] = [
  ...ORDER_PATH_DEFINITIONS.map((definition) => ({
    key: `id:${definition.id}`,
    label: definition.label,
  })),
];

export const GROUPED_DEFAULT_ORDER_PATH_OPTIONS: OrderPathOption[] = [
  {
    key: `group:${OWNED_MALL_ORDER_PATH_GROUP.key}`,
    label: OWNED_MALL_ORDER_PATH_GROUP.label,
  },
];

export const REFUND_OWNED_MALL_NOTICE =
  "환불 합계는 카페24(PC 쇼핑몰·모바일웹) 데이터만 집계합니다.";

function readText(value: unknown): string {
  return typeof value === "string" || typeof value === "number"
    ? String(value).trim()
    : "";
}

function getOrderPathSource(item: any): any {
  return item?.order && typeof item.order === "object" ? item.order : item;
}

function findDefinition(id: string): OrderPathDefinition | undefined {
  return ORDER_PATH_DEFINITIONS.find((definition) => definition.id === id);
}

function findGroup(id: string): OrderPathGroupDefinition | undefined {
  return ORDER_PATH_GROUPS.find((group) => group.memberIds.includes(id));
}

export function resolveOrderPath(
  item: any,
  presentationMode: OrderPathPresentationMode = "grouped"
): ResolvedOrderPath {
  const source = getOrderPathSource(item);
  const sourceId = readText(source?.order_place_id);
  const sourceName = readText(source?.order_place_name);
  const definition = findDefinition(sourceId);
  const fallbackName =
    sourceId.toUpperCase() === "NCHECKOUT" ? "네이버 페이" : "";
  const sourceLabel = sourceId
    ? definition?.label || sourceName || fallbackName || `주문경로 ${sourceId}`
    : "주문경로 정보 없음";
  const group =
    presentationMode === "grouped" ? findGroup(sourceId) : undefined;

  if (group) {
    const feeSource = findDefinition(group.feeSourceId);
    return {
      sourceId,
      sourceLabel,
      key: `group:${group.key}`,
      label: group.label,
      feePathCode: feeSource?.feePathCode || group.feeSourceId,
    };
  }

  return {
    sourceId,
    sourceLabel,
    key: sourceId ? `id:${sourceId}` : "missing:id",
    label: sourceLabel,
    feePathCode: definition?.feePathCode || sourceId,
  };
}

export function getDefaultOrderPathOptions(
  presentationMode: OrderPathPresentationMode = "grouped"
): OrderPathOption[] {
  return presentationMode === "grouped"
    ? GROUPED_DEFAULT_ORDER_PATH_OPTIONS
    : DEFAULT_ORDER_PATH_OPTIONS;
}

export function normalizeOrderPathSelection(
  selectedKeys: string[],
  presentationMode: OrderPathPresentationMode
): string[] {
  const keys = new Set(Array.isArray(selectedKeys) ? selectedKeys : []);
  const groupKey = `group:${OWNED_MALL_ORDER_PATH_GROUP.key}`;
  const memberKeys = OWNED_MALL_ORDER_PATH_GROUP.memberIds.map(
    (id) => `id:${id}`
  );

  if (presentationMode === "grouped") {
    let hasMember = false;
    memberKeys.forEach((key) => {
      if (keys.delete(key)) hasMember = true;
    });
    if (hasMember) keys.add(groupKey);
  } else if (keys.delete(groupKey)) {
    memberKeys.forEach((key) => keys.add(key));
  }

  return Array.from(keys);
}

export function transformOrderPathOptions(
  options: OrderPathOption[],
  presentationMode: OrderPathPresentationMode
): OrderPathOption[] {
  const ownedMallKeys = new Set([
    `group:${OWNED_MALL_ORDER_PATH_GROUP.key}`,
    ...OWNED_MALL_ORDER_PATH_GROUP.memberIds.map((id) => `id:${id}`),
  ]);
  const additionalOptions = (Array.isArray(options) ? options : []).filter(
    (option) => !ownedMallKeys.has(option.key)
  );
  return [
    ...getDefaultOrderPathOptions(presentationMode),
    ...additionalOptions,
  ];
}

/**
 * Refunds preserve their original order under `order`, while normal orders
 * expose the path at the top level. Keep both shapes on the same key so one
 * selected path always affects its payments and refunds together.
 */
export function getOrderPathOption(
  item: any,
  presentationMode: OrderPathPresentationMode = "grouped"
): OrderPathOption {
  const { key, label } = resolveOrderPath(item, presentationMode);
  return { key, label };
}

export function collectOrderPathOptions(
  items: any[],
  presentationMode: OrderPathPresentationMode = "grouped"
): OrderPathOption[] {
  const defaultOptions = getDefaultOrderPathOptions(presentationMode);
  const byKey = new Map<string, OrderPathOption>(
    defaultOptions.map((option) => [option.key, option])
  );
  (Array.isArray(items) ? items : []).forEach((item) => {
    const resolved = resolveOrderPath(item, presentationMode);
    const option = { key: resolved.key, label: resolved.label };
    if (!byKey.has(option.key)) byKey.set(option.key, option);
  });
  const fixedKeys = new Set(defaultOptions.map((option) => option.key));
  const additionalOptions = Array.from(byKey.values())
    .filter((option) => !fixedKeys.has(option.key))
    .sort((a, b) => a.label.localeCompare(b.label, "ko"));

  return [...defaultOptions, ...additionalOptions];
}

export function matchesOrderPathFilter(
  item: any,
  filter?: OrderPathFilter
): boolean {
  const selectedKeys = filter?.selectedKeys || [];
  if (selectedKeys.length === 0) return true;

  const presentationMode =
    filter?.presentationMode ||
    (selectedKeys.some((k) => k.startsWith("id:")) ? "separate" : "grouped");

  const isSelected = selectedKeys.includes(
    resolveOrderPath(item, presentationMode).key
  );
  return filter?.mode === "exclude" ? !isSelected : isSelected;
}

export function filterByOrderPath(
  items: any[],
  filter?: OrderPathFilter
): any[] {
  return (Array.isArray(items) ? items : []).filter((item) =>
    matchesOrderPathFilter(item, filter)
  );
}

/** Returns a display-only filtered result; the cached source row is never changed. */
export function filterSettlementResult<T extends Record<string, any>>(
  result: T,
  filter?: OrderPathFilter
): T {
  const orders = filterByOrderPath(result?.orders, filter);
  const refunds = filterByOrderPath(result?.refunds, filter);
  const paymentSummary = summarizeOrderPayments(orders);
  const paymentCompositionAmount =
    summarizeOrderSpecifiedPaymentTotal(orders).totalAmount;
  const refundSummary = summarizeRefundAmounts(refunds);

  return {
    ...result,
    orders,
    refunds,
    payment_summary: paymentSummary,
    order_count: paymentSummary.orderCount,
    payment_composition_amount: paymentCompositionAmount,
    refund_count: refunds.length,
    refund_amount: refundSummary.amount,
    refund_breakdown: refundSummary.breakdown,
    total_count: paymentSummary.orderCount + refunds.length,
    total_amount: paymentCompositionAmount + refundSummary.amount,
  };
}

export function getNaverPoint(item: any): number {
  const raw =
    item?.naver_point ??
    item?.actual_order_amount?.naver_point ??
    item?.initial_order_amount?.naver_point ??
    item?.order?.naver_point ??
    item?.order?.actual_order_amount?.naver_point ??
    item?.order?.initial_order_amount?.naver_point ??
    0;
  const num = Number(String(raw).replace(/,/g, "").trim());
  return Number.isFinite(num) ? num : 0;
}

export function isNaverPayOrder(
  item: any,
  presentationMode: OrderPathPresentationMode = "grouped"
): boolean {
  if (!item) return false;
  const source = getOrderPathSource(item);
  const sourceId = readText(source?.order_place_id).toLowerCase();
  const sourceName = readText(source?.order_place_name).toLowerCase();
  if (
    sourceId === "ncheckout" ||
    sourceId.includes("naver") ||
    sourceName.includes("네이버") ||
    sourceName.includes("naver")
  ) {
    return true;
  }

  const resolved = resolveOrderPath(item, presentationMode);
  const label = (resolved.label || "").toLowerCase();
  const sourceLabel = (resolved.sourceLabel || "").toLowerCase();
  const key = (resolved.key || "").toLowerCase();

  return (
    label.includes("네이버") ||
    label.includes("naver") ||
    sourceLabel.includes("네이버") ||
    sourceLabel.includes("naver") ||
    key.includes("ncheckout") ||
    key.includes("naver")
  );
}

export function isNaverPayFilterActive(
  filter?: OrderPathFilter,
  options?: OrderPathOption[]
): boolean {
  if (
    !filter ||
    !Array.isArray(filter.selectedKeys) ||
    filter.selectedKeys.length === 0
  ) {
    return true;
  }

  const isNaverKey = (k: string) => {
    const lower = k.toLowerCase();
    if (lower.includes("ncheckout") || lower.includes("naver")) return true;
    if (options) {
      const opt = options.find((o) => o.key === k);
      if (
        opt &&
        (opt.label.toLowerCase().includes("네이버") ||
          opt.label.toLowerCase().includes("naver"))
      ) {
        return true;
      }
    }
    return false;
  };

  if (filter.mode === "exclude") {
    return !filter.selectedKeys.some(isNaverKey);
  }
  return filter.selectedKeys.some(isNaverKey);
}
